/**
 * PosteingangScreen - die Eingangsliste (epic B2, Detailkonzept 8.1).
 *
 * Every Eingang over all four channels, with Kanal, Bearbeitung (task-derived), Konfidenz
 * and the Eingang's own Status (offen / erledigt, one click) per row. Shared DataTable.
 *
 * Row and Betreff both open the Bearbeitungsmaske (8.1's Ausgangspunkt).
 *
 * PosteingangPage owns the route and the page chrome; this owns filters + table.
 *
 * The list pages to the end of the bucket, never to the first 50: the visibility gate
 * can hide rows inside a page, so a short page is not the end - only a null cursor is.
 *
 * Unread is per viewer (`posteingang_read`, A4): an unread row carries a dot and a bold
 * Betreff, the mail-client convention. The mark itself is written when the Eingang is
 * opened - see BearbeitungsMaskeScreen - so this only renders what the row says.
 *
 * [Source: docs/eingangsverarbeitung-epic.md §3 WS-B B2]
 */
import { FileText, Globe, Mail, Paperclip, Upload } from 'lucide-react';
import { useMemo, useState, type ComponentType } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import {
  POSTEINGANG_KANAL_LABELS,
  POSTEINGANG_KANAL_VALUES,
  POSTEINGANG_STATE,
  POSTEINGANG_STATE_LABELS,
  POSTEINGANG_STATE_VALUES,
  POSTEINGANG_STATUS_LABELS,
  type PosteingangItem,
  type PosteingangKanal,
  type PosteingangState,
} from '@brokly/shared';
import { cn } from '../../lib/cn';
import { DataTable } from '../../components/data/DataTable';
import { InfiniteScrollSentinel } from '../../components/data/InfiniteScrollSentinel';
import { Badge } from '../../components/ui/Badge';
import { EntityLink } from '../../components/ui/EntityLink';
import { FilterChip } from '../../components/ui/FilterChip';
import { StatusDot } from '../../components/ui/StatusDot';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/Tabs';
import { KonfidenzBadge } from '../communication-v2/components/konfidenz';
import { dateTimeShortDe } from '../../lib/format';
import { POSTEINGANG_STATUS_TONE, konfidenzPercent } from './api/posteingang-api';
import { usePosteingang, usePosteingangCounts } from './hooks/usePosteingang';
import { EingangStatusSelect } from './components/EingangStatusSelect';
import { SearchBox } from '../../components/data/SearchBox';
import { SegmentedControl } from '../../components/ui/SegmentedControl';
import { SemanticDegradedNotice } from '../search/SemanticDegradedNotice';
import {
  POSTEINGANG_SEARCH_DEBOUNCE_MS,
  POSTEINGANG_SEARCH_MIN_LENGTH,
  useDebounced,
  usePosteingangSemantic,
} from './hooks/usePosteingangSearch';

/** The search bar's two modes, the Ablage's Volltext / Semantisch. */
type SearchMode = 'volltext' | 'semantisch';

/**
 * Kanal -> icon, the KANAL_META pattern. A separate map because the Posteingang's four
 * channels are not the mail screen's four (`scanning_center` and `upload` exist here,
 * `telefon` does not), and the shared contract owns the LABELS - only the icon is ours.
 */
const KANAL_ICON: Record<PosteingangKanal, ComponentType<{ className?: string }>> = {
  email: Mail,
  scanning_center: FileText,
  upload: Upload,
  portal: Globe,
};

/**
 * The Aufgaben cell in ONE phrase, picked in the order a broker triages by: what still
 * needs a person, then what the KI did unattended, then what is closed. The raw
 * five-number breakdown belongs in the Bearbeitungsmaske, not in a list cell.
 */
function aufgabenLabel(tasks: PosteingangItem['tasks']): string | null {
  if (tasks.total === 0) return null;
  if (tasks.offen > 0) return `${tasks.offen} offen`;
  if (tasks.automatisch > 0) return `${tasks.automatisch} automatisch`;
  return `${tasks.erledigt} erledigt`;
}

/** A Kunde cell is a real entity link only when the Eingang is routed to one (8.1). */
function kundeIsLinkable(
  item: PosteingangItem,
): item is PosteingangItem & { subject_id: string; kunde_name: string } {
  return (
    !!item.kunde_name &&
    !!item.subject_id &&
    (item.subject_type === 'firma' || item.subject_type === 'person')
  );
}

/** Column factory. The Betreff is a link to the Bearbeitungsmaske (keyboard access). */
function makeColumns(
  onOpenEingang: (commId: string) => void,
  onOpenKunde: (subjectType: string, id: string) => void,
  /** Semantisch search: the attachment that matched, per Eingang ("Treffer im Anhang"). */
  hitFiles: ReadonlyMap<string, string | null> = new Map(),
): ColumnDef<PosteingangItem>[] {
  return [
    {
      accessorKey: 'kanal',
      header: 'Kanal',
      cell: ({ row }) => {
        const KanalIcon = KANAL_ICON[row.original.kanal];
        return (
          <span className="inline-flex items-center gap-1.5 rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground">
            <KanalIcon className="size-3.5 shrink-0" />
            {POSTEINGANG_KANAL_LABELS[row.original.kanal]}
          </span>
        );
      },
    },
    {
      accessorKey: 'subject',
      header: 'Betreff',
      cell: ({ row }) => (
        // The one wide column: `whitespace-nowrap` comes from TableCell, so the cell
        // opts out of it and truncates instead of forcing the table wider than the page.
        <div className="max-w-[22rem] whitespace-normal leading-tight">
          {/* Unread: a dot plus a bold Betreff. Absent `unread` is treated as READ - a
              row must never claim a broker has not seen a mail on a missing field. */}
          <div className="flex items-center gap-1.5">
            {row.original.unread === true && (
              <span
                aria-label="Ungelesen"
                title="Ungelesen"
                className="size-1.5 shrink-0 rounded-full bg-[hsl(var(--primary))]"
              />
            )}
            <EntityLink
              onActivate={() => onOpenEingang(row.original.id)}
              title="Eingang in der Bearbeitungsmaske öffnen"
            >
              <span
                className={cn(
                  'line-clamp-1 text-left',
                  row.original.unread === true && 'font-semibold',
                )}
              >
                {row.original.subject ?? '(kein Betreff)'}
              </span>
            </EntityLink>
          </div>
          {row.original.from && (
            <div className="line-clamp-1 text-xs text-muted-foreground">{row.original.from}</div>
          )}
          {hitFiles.get(row.original.id) && (
            <div className="line-clamp-1 text-xs text-muted-foreground">
              Treffer im Anhang: <span className="text-foreground">{hitFiles.get(row.original.id)}</span>
            </div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'kunde_name',
      header: 'Kunde',
      cell: ({ row }) => {
        const item = row.original;
        /* An unconfirmed record IS a customer record, so the wire sends it with the same
           subject columns as a known client and `unconfirmed` set as well. The marker
           therefore rides ALONGSIDE the link: in front of the linkable branch an unconfirmed
           record would lose its link, behind it the badge would never render.
           `unconfirmed.name` empty means no bare badge. */
        const isUnconfirmed = Boolean(item.unconfirmed?.name);
        if (kundeIsLinkable(item)) {
          return (
            <span className="inline-flex items-center gap-1.5">
              {isUnconfirmed && <Badge variant="neutral">Unbestätigt</Badge>}
              <EntityLink onActivate={() => onOpenKunde(item.subject_type!, item.subject_id)}>
                {item.kunde_name}
              </EntityLink>
            </span>
          );
        }
        if (isUnconfirmed) {
          return (
            <span className="inline-flex items-center gap-1.5">
              <Badge variant="neutral">Unbestätigt</Badge>
              <span className="text-foreground">{item.unconfirmed!.name}</span>
            </span>
          );
        }
        // No Kunde yet is a REAL state here (the KI found no match) — 8.1's «Route zu
        // Kunde» case. It reads as a gap, not as an empty string.
        return <span className="text-muted-foreground">{item.kunde_name ?? 'Nicht zugeordnet'}</span>;
      },
    },
    {
      accessorKey: 'received_at',
      header: 'Eingegangen',
      sortingFn: (a, b) => Date.parse(a.original.received_at) - Date.parse(b.original.received_at),
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {dateTimeShortDe(row.original.received_at)}
        </span>
      ),
    },
    {
      accessorKey: 'attachment_count',
      header: 'Anhänge',
      cell: ({ row }) =>
        row.original.attachment_count > 0 ? (
          <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
            <Paperclip className="size-3.5" />
            {row.original.attachment_count}
          </span>
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
    {
      id: 'aufgaben',
      header: 'Aufgaben',
      cell: ({ row }) => {
        const label = aufgabenLabel(row.original.tasks);
        return label ? (
          <span className="text-sm text-foreground">{label}</span>
        ) : (
          <span className="text-muted-foreground">-</span>
        );
      },
    },
    {
      // Task-derived state, renamed so the table has only one "Status" column.
      accessorKey: 'status',
      header: 'Bearbeitung',
      cell: ({ row }) => {
        const konfidenz = konfidenzPercent(row.original.confidence);
        return (
          <div className="flex items-center gap-2">
            <StatusDot
              tone={POSTEINGANG_STATUS_TONE[row.original.status]}
              label={POSTEINGANG_STATUS_LABELS[row.original.status]}
            />
            {konfidenz !== null && <KonfidenzBadge value={konfidenz} />}
          </div>
        );
      },
    },
    {
      // Stored Eingang status (0141); the Offen/Erledigt tabs filter on it.
      accessorKey: 'eingang_status',
      header: 'Status',
      cell: ({ row }) => (
        <EingangStatusSelect commId={row.original.id} status={row.original.eingang_status} />
      ),
    },
  ];
}

interface PosteingangScreenProps {
  state: PosteingangState;
  kanal: PosteingangKanal | null;
  onStateChange: (state: PosteingangState) => void;
  onKanalChange: (kanal: PosteingangKanal | null) => void;
  /** Row or Betreff click: the Bearbeitungsmaske for this Eingang. */
  onOpenEingang: (commId: string) => void;
  onOpenKunde: (subjectType: string, id: string) => void;
}

export function PosteingangScreen({
  state,
  kanal,
  onStateChange,
  onKanalChange,
  onOpenEingang,
  onOpenKunde,
}: PosteingangScreenProps) {
  // P2 hardening 04: the "Private Absender" review toggle — show ONLY the rows hidden for the
  // caller (privat markierte Absender + firmenweit ausgeblendete bisherige).
  const [privateSenders, setPrivateSenders] = useState(false);
  // The page's own search (Volltext or Semantisch), same controls as the Ablage.
  const [search, setSearch] = useState('');
  const [mode, setMode] = useState<SearchMode>('volltext');
  const needle = useDebounced(search.trim(), POSTEINGANG_SEARCH_DEBOUNCE_MS);
  const searchActive = needle.length >= POSTEINGANG_SEARCH_MIN_LENGTH;
  const semantic = usePosteingangSemantic(needle, searchActive && mode === 'semantisch');
  // Semantisch with the embed service down falls back to Volltext and says so (the Ablage rule).
  const semanticDegraded = searchActive && mode === 'semantisch' && !semantic.semanticAvailable;
  const useSemanticHits = searchActive && mode === 'semantisch' && !semanticDegraded;
  const listSearch = !searchActive
    ? {}
    : useSemanticHits
      ? { attachment_hits: (semantic.hits ?? []).map((h) => h.id) }
      : { q: needle };

  const { data: rawItems, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    usePosteingang(state, kanal, listSearch, { private_senders: privateSenders });
  // Semantisch hits keep their score order; the list itself is newest first.
  const items = useMemo(() => {
    if (!useSemanticHits || !semantic.hits) return rawItems;
    const rank = new Map(semantic.hits.map((h, i) => [h.id, i]));
    return [...rawItems].sort((a, b) => (rank.get(a.id) ?? 0) - (rank.get(b.id) ?? 0));
  }, [rawItems, useSemanticHits, semantic.hits]);
  const hitFiles = useMemo(
    () => new Map(useSemanticHits ? (semantic.hits ?? []).map((h) => [h.id, h.filename] as const) : []),
    [useSemanticHits, semantic.hits],
  );
  // The same hook the sidebar reads, so the two badges are one number, never two rules.
  // Deliberately NOT scoped by the Kanal filter: it counts the whole bucket, like `offen`.
  const { data: counts } = usePosteingangCounts();
  // Memoised: `columnDef.cell` is an inline component, so an unstable columns array
  // remounts every cell on every render (AufgabenScreen precedent).
  const columns = useMemo(
    () => makeColumns(onOpenEingang, onOpenKunde, hitFiles),
    [onOpenEingang, onOpenKunde, hitFiles],
  );

  return (
    <div className="flex w-full flex-col gap-3">
      {/* Offen / Erledigt, with the Kanal filter underneath (epic B2). The app's own
          underline Tabs — the two-state view switch above the table, the Kanal chips
          the second dimension under it. */}
      <div className="border-b border-border">
        <Tabs value={state} onValueChange={(v) => onStateChange(v as PosteingangState)}>
          <TabsList variant="underline" className="border-b-0">
            {POSTEINGANG_STATE_VALUES.map((value) => (
              <TabsTrigger key={value} value={value}>
                {POSTEINGANG_STATE_LABELS[value]}
                {/* The unread badge (B2). Only on Offen: `unread` is counted as «offen
                    AND unread», so hanging it on Erledigt would badge a tab with a number
                    about the other one. Absent or 0 renders nothing - the «honest or
                    absent» rule, and a broker who is through their mail wants no zero. */}
                {value === POSTEINGANG_STATE.OFFEN && (counts?.unread ?? 0) > 0 && (
                  <span
                    aria-label={`${counts!.unread} ungelesen`}
                    className="ml-1.5 rounded-full bg-[hsl(var(--primary))] px-1.5 py-0.5 text-[11px] font-medium leading-none text-[hsl(var(--primary-foreground))]"
                  >
                    {counts!.unread}
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="flex flex-wrap items-center gap-1.5 pb-2.5 pt-1.5">
          <FilterChip
            label="Alle Kanäle"
            active={kanal === null}
            onClick={() => onKanalChange(null)}
          />
          {POSTEINGANG_KANAL_VALUES.map((value) => (
            <FilterChip
              key={value}
              label={POSTEINGANG_KANAL_LABELS[value]}
              active={kanal === value}
              onClick={() => onKanalChange(value)}
            />
          ))}
          {/* P2 hardening 04: the right-aligned "Private Absender" review toggle. */}
          <div className="ml-auto">
            <FilterChip
              label="Private Absender"
              active={privateSenders}
              onClick={() => setPrivateSenders((v) => !v)}
            />
          </div>
        </div>
      </div>

      {privateSenders && (
        <p className="rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground" role="note">
          Diese E-Mails sind für Sie ausgeblendet (privat markierte Absender oder firmenweit
          blockierte, bisherige E-Mails).
        </p>
      )}

      {/* The search bar: Volltext / Semantisch, the Ablage's field and toggle. */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <SearchBox
            value={search}
            onChange={setSearch}
            ariaLabel="Posteingang durchsuchen"
            placeholder={
              mode === 'semantisch'
                ? 'Semantisch in Anhängen suchen …'
                : 'Absender, Kunde, Betreff, Inhalt, Anhang …'
            }
          />
          <SegmentedControl
            ariaLabel="Suchmodus"
            value={mode}
            onValueChange={(v) => setMode(v as SearchMode)}
            options={[
              { value: 'volltext', label: 'Volltext' },
              { value: 'semantisch', label: 'Semantisch' },
            ]}
          />
          <div className="flex-1" />
          {searchActive && !isLoading && !semantic.isLoading && (
            <span className="text-xs text-muted-foreground [font-variant-numeric:tabular-nums]">
              {items.length === 1 ? '1 Treffer' : `${items.length} Treffer`}
            </span>
          )}
        </div>
        {semanticDegraded && <SemanticDegradedNotice />}
      </div>

      {/* The empty state waits for the paging to finish. A keyset page can come back
          empty with a cursor still set, and «Keine Eingänge» over a list that is still
          loading its next page would be a false answer. */}
      {items.length === 0 && !hasNextPage && !isLoading && !semantic.isLoading ? (
        <p className="rounded-xl border border-border bg-card px-3 py-10 text-center text-sm text-muted-foreground">
          {searchActive ? `Keine Treffer für «${needle}».` : 'Keine Eingänge in dieser Ansicht.'}
        </p>
      ) : (
        <>
          {items.length > 0 && (
            <DataTable
              columns={columns}
              data={items}
              onRowClick={(item) => onOpenEingang(item.id)}
            />
          )}
          <InfiniteScrollSentinel
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={fetchNextPage}
          />
        </>
      )}
    </div>
  );
}
